import type { Middleware } from '@reduxjs/toolkit'
import { loadTrashContent } from '~/API/job'
import { FileUploadSucceeded } from '../uploader/actions'
import { BulkOfActions, isType } from '../common/actions'
import { FilesDeletionSucceeded } from '../job/actions'
import { getTimelineJobID } from '../timeline/selectors'
import type { StateWithTimeline } from '../timeline/reducers'
import { FetchMoreTrashTriggered, TrashContentOutdated } from './actions'
import { getTrashFilesNextResultOffset } from './selectors'
import type { StateWithTrash } from './reducer'

type TrashMiddlewareState = StateWithTimeline & StateWithTrash

// sinon does not support ESModule stub,
// using factory function in unit tests to stub loadTrashContentFunc
export const makeTrashMiddleware =
    (loadTrashContentFunc = loadTrashContent): Middleware =>
    (store) =>
    (next) =>
    (action) => {
        const state: TrashMiddlewareState = store.getState()
        const timelineID = getTimelineJobID(state)

        if (isType(action, BulkOfActions)) {
            const filesDeletedAction = action.payload.filter(
                (a): a is ReturnType<typeof FilesDeletionSucceeded> =>
                    isType(a, FilesDeletionSucceeded),
            )[0]
            if (
                filesDeletedAction &&
                filesDeletedAction.payload.jobID === timelineID
            ) {
                store.dispatch(TrashContentOutdated())
            }
        }

        // if deletion comes separate from BulkOfActions
        if (
            isType(action, FilesDeletionSucceeded) &&
            action.payload.jobID === timelineID
        ) {
            store.dispatch(TrashContentOutdated())
        }

        // Handling the case when uploading a deleted file
        if (isType(action, FileUploadSucceeded)) {
            store.dispatch(TrashContentOutdated())
        }

        if (isType(action, FetchMoreTrashTriggered)) {
            const offset = getTrashFilesNextResultOffset(state)
            loadTrashContentFunc(store.dispatch, offset)
        }

        next(action)
    }

export const trashMiddleware = makeTrashMiddleware()
